import { useCallback, useState } from 'react';
import { App, Modal } from 'antd';
import type { UploadProps } from 'antd/es/upload';
import api from '@/services/api.service';
import { postExcelImportWithRetry } from '@/utils/excelImportUpload';

function triggerBlobDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export type ExcelMigrationIoOptions = {
  templateUrl: string;
  exportUrl: string;
  importUrl: string;
  templateFilename: string;
  exportFilenamePrefix: string;
  onImportSuccess?: () => void;
  buildExportParams?: () => Record<string, string | number | boolean | undefined>;
};

export function useExcelMigrationIo(options: ExcelMigrationIoOptions) {
  const { message } = App.useApp();
  const [isExcelBusy, setIsExcelBusy] = useState(false);
  const [uploadPct, setUploadPct] = useState<number | null>(null);

  const downloadTemplate = useCallback(async () => {
    setIsExcelBusy(true);
    try {
      const res = await api.getClient().get(options.templateUrl, { responseType: 'blob' });
      triggerBlobDownload(
        new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        options.templateFilename
      );
      message.success('Template downloaded');
    } catch (err: any) {
      message.error(err?.response?.data?.message || err?.message || 'Download failed');
    } finally {
      setIsExcelBusy(false);
    }
  }, [message, options.templateFilename, options.templateUrl]);

  const downloadExport = useCallback(async () => {
    setIsExcelBusy(true);
    try {
      const params = options.buildExportParams?.() ?? {};
      const qs = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => {
        if (v != null && v !== '') qs.append(k, String(v));
      });
      const url = qs.toString() ? `${options.exportUrl}?${qs}` : options.exportUrl;
      const res = await api.getClient().get(url, { responseType: 'blob' });
      const dateStr = new Date().toISOString().slice(0, 10);
      triggerBlobDownload(
        new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        `${options.exportFilenamePrefix}-${dateStr}.xlsx`
      );
      message.success('Export downloaded');
    } catch (err: any) {
      message.error(err?.response?.data?.message || err?.message || 'Export failed');
    } finally {
      setIsExcelBusy(false);
    }
  }, [message, options]);

  const handleImport: UploadProps['customRequest'] = useCallback(
    async (opts) => {
      const { file, onError, onSuccess } = opts;
      const formData = new FormData();
      formData.append('file', file as File);
      setIsExcelBusy(true);
      setUploadPct(0);
      try {
        const res = await postExcelImportWithRetry(api.getClient(), options.importUrl, formData, {
          onUploadProgress: (p) => setUploadPct(p),
        });
        const body = res.data as {
          success?: boolean;
          message?: string;
          data?: { created: number; updated: number; errors?: string[] };
        };
        if (body?.success !== false) {
          const d = body?.data;
          const errList = d?.errors?.length ? ` (${d.errors.length} row message(s))` : '';
          message.success((body?.message || 'Import completed') + errList);
          if (d?.errors?.length) {
            const errText = d.errors.slice(0, 50).join('\n');
            const suffix = d.errors.length > 50 ? '\n…and more' : '';
            Modal.warning({
              title: 'Import notes',
              width: 560,
              content: errText + suffix,
            });
          }
          options.onImportSuccess?.();
          onSuccess?.(body);
        } else {
          throw new Error(body?.message || 'Import failed');
        }
      } catch (err: any) {
        message.error(err?.response?.data?.message || err?.message || 'Import failed');
        onError?.(err);
      } finally {
        setIsExcelBusy(false);
        setUploadPct(null);
      }
    },
    [message, options]
  );

  return { isExcelBusy, uploadPct, downloadTemplate, downloadExport, handleImport };
}
